import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, Flight, ViewList, ViewModule } from '@mui/icons-material';
import { RootState } from '../../store/store';
import { matchesOwnershipFilter, setFleetLayout } from '../../store/slices/dronesSlice';
import { Drone, droneLabel } from '../../types/drone';
import { FleetLayout } from '../../types/user';
import { usePreferenceWriter } from '../../hooks/useCurrentUser';
import { FleetOwnershipFilter } from './FleetOwnershipFilter';
import { DroneImage } from '../DronePanel/DroneImage';
import { DroneStatusChips } from '../DronePanel/DroneStatusChips';
import { formatDroneStatus } from '../../utils/formatters';
import { DroneReportCard } from './DroneReportCard';
import { CredentialsBanner } from './CredentialsBanner';
import { CredentialsChip } from './CredentialsChip';

/** By what people call them, then by ID, so the order does not shuffle on a refetch. */
const byLabel = (a: Drone, b: Drone) =>
  droneLabel(a).localeCompare(droneLabel(b)) || a.id.localeCompare(b.id);

const ListLayout: React.FC<{ drones: Drone[]; onOpen: (id: string) => void }> = ({
  drones,
  onOpen,
}) => (
  <Paper>
    <List aria-label="Drones">
      {drones.map((drone) => (
        <ListItemButton key={drone.id} onClick={() => onOpen(drone.id)}>
          <ListItemAvatar>
            <Avatar variant="rounded" aria-label={`No photo of ${droneLabel(drone)}`}>
              <Flight />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={droneLabel(drone)}
            // The ID under a name, so two drones with one name can be told
            // apart (G2: names are not unique).
            secondary={
              drone.name
                ? `${formatDroneStatus(drone.status)} · ${drone.id}`
                : formatDroneStatus(drone.status)
            }
            primaryTypographyProps={{ sx: { overflowWrap: 'anywhere' } }}
          />
          <CredentialsChip drone={drone} />
        </ListItemButton>
      ))}
    </List>
  </Paper>
);

const GridLayout: React.FC<{ drones: Drone[]; onOpen: (id: string) => void }> = ({
  drones,
  onOpen,
}) => (
  <Box
    role="list"
    aria-label="Drones"
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      gap: 2,
    }}
  >
    {drones.map((drone) => (
      <Card key={drone.id} role="listitem">
        <CardActionArea onClick={() => onOpen(drone.id)}>
          <DroneImage imageUrl={null} droneName={droneLabel(drone)} height={140} />
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" component="h3" sx={{ overflowWrap: 'anywhere' }}>
              {droneLabel(drone)}
            </Typography>
            {drone.name && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {drone.id}
              </Typography>
            )}
            <DroneStatusChips drone={drone} />
            <CredentialsChip drone={drone} />
          </CardContent>
        </CardActionArea>
      </Card>
    ))}
  </Box>
);

/**
 * The Fleet page (#412, G7): every drone the viewer may see, parked or flying,
 * as rows or tiles, filtered by owner like the map (#385).
 *
 * The map shows only what is in the air; this is where a parked drone is found.
 * A drone opens as its report card (#411).
 */
export const FleetGallery: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreenCard = useMediaQuery(theme.breakpoints.down('sm'));
  const { drones, loading, error, ownershipFilter, fleetLayout } = useSelector(
    (state: RootState) => state.drones
  );
  const { loaded, savePreference } = usePreferenceWriter();
  const [openId, setOpenId] = useState<string | null>(null);

  const all = useMemo(() => Object.values(drones), [drones]);
  const shown = useMemo(
    () => all.filter((d) => matchesOwnershipFilter(d, ownershipFilter)).sort(byLabel),
    [all, ownershipFilter]
  );
  const open = openId ? drones[openId] : undefined;

  const changeLayout = (_e: React.MouseEvent<HTMLElement>, next: FleetLayout | null) => {
    // Clicking the pressed button emits null; keep the layout there is.
    if (next === null) return;
    dispatch(setFleetLayout(next));
    // Only once the stored document has arrived, as the ownership filter does:
    // saving before then would write the defaults over every other setting.
    if (loaded) {
      void savePreference({ fleet: { layout: next } });
    }
  };

  let body: React.ReactNode;
  if (loading && all.length === 0) {
    body = (
      <Box aria-busy="true" aria-label="Loading drones" sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={240} />
        ))}
      </Box>
    );
  } else if (error && all.length === 0) {
    body = <Alert severity="error">The fleet could not be loaded: {error}</Alert>;
  } else if (all.length === 0) {
    body = (
      <Alert severity="info">
        No drones yet. Register one from the map, or add a virtual drone.
      </Alert>
    );
  } else if (shown.length === 0) {
    body = <Alert severity="info">No drones match this filter.</Alert>;
  } else if (fleetLayout === 'list') {
    body = <ListLayout drones={shown} onOpen={setOpenId} />;
  } else {
    body = <GridLayout drones={shown} onOpen={setOpenId} />;
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Fleet
        </Typography>
        <FleetOwnershipFilter />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={fleetLayout}
          onChange={changeLayout}
          aria-label="Layout"
        >
          <ToggleButton value="list" aria-label="List">
            <ViewList />
          </ToggleButton>
          <ToggleButton value="grid" aria-label="Tiles">
            <ViewModule />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* A refetch that failed while drones are already shown is said here,
          rather than replacing a fleet the pilot can still use. */}
      {error && all.length > 0 && (
        <Alert severity="warning">The fleet could not be refreshed: {error}</Alert>
      )}

      {/* Every drone the viewer may see, not only the filtered ones: a lapse
          hidden by a filter is still a lapse (#777, C16). */}
      <CredentialsBanner drones={all} />

      {body}

      <Dialog
        open={open !== undefined}
        onClose={() => setOpenId(null)}
        fullScreen={fullScreenCard}
        fullWidth
        maxWidth="sm"
        aria-labelledby="drone-report-card-title"
      >
        <DialogTitle id="drone-report-card-title" sx={{ display: 'flex', alignItems: 'center' }}>
          <Box component="span" sx={{ flexGrow: 1 }}>
            Drone report
          </Box>
          <IconButton onClick={() => setOpenId(null)} aria-label="Close drone report">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>{open && <DroneReportCard drone={open} />}</DialogContent>
      </Dialog>
    </Box>
  );
};
